/** @format */
'use client';
import { TableKey } from '@common/application/enums/TableKey';
import useTableSorting from '@common/application/hooks/useTableSorting/useTableSorting';
import { ElasticPageHistory, ElasticResponseBody, ElasticSearchAfter } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';
import { useCallback, useRef, useState } from 'react';

const useSearchableTable = <DataType>(tableKey: TableKey, config?: { throttle: boolean }) => {
  // Elasticsearch Point In Time
  const [pit, _setPit] = useState<Nullable<string>>(null);
  const [searchAfter, setSearchAfter] = useState<Nullable<ElasticSearchAfter>>(null);
  const [pageHistory, setPageHistory] = useState<ElasticPageHistory>([]);
  const [search, _setSearch] = useState('');
  const ignoredPitIdRef = useRef<Nullable<string>>(null);
  const [sort, setSort] = useTableSorting(tableKey, search);

  //   Reset pagination on search
  const setSearch = useCallback((value: string) => {
    _setSearch(value);
    setSearchAfter(null);
    setPageHistory([]);
  }, []);

  // set Pit
  const setPit = useCallback(
    function (data: ElasticResponseBody<DataType> | null | undefined) {
      if (data?.pit_id) {
        const nextPit = JSON.stringify({ id: data.pit_id });

        // Ignore a single stale rebound from placeholderData after explicit invalidation.
        if (ignoredPitIdRef.current && nextPit === ignoredPitIdRef.current) {
          ignoredPitIdRef.current = null;
          return;
        }

        ignoredPitIdRef.current = null;
        _setPit(nextPit);
      } else {
        ignoredPitIdRef.current = pit;
        _setPit(null);
      }
    },
    [pit],
  );

  const nextPage = useCallback(
    (data: ElasticResponseBody<DataType>) => {
      const s = data.payload.slice(-1)[0].sort;
      setPageHistory((pageHistory) => [...pageHistory, searchAfter]);
      setSearchAfter(s);
    },
    [searchAfter],
  );

  const previousPage = useCallback(() => {
    setSearchAfter(() => pageHistory.slice(-1)[0]);
    setPageHistory((pageHistory) => pageHistory.slice(0, -1));
  }, [pageHistory]);

  return { search, setSearch, pit, setPit, nextPage, previousPage, sort, setSort, searchAfter, setSearchAfter, pageHistory, setPageHistory };
};

export default useSearchableTable;
